import { useEffect, useState, useRef } from "react";
import { useTheme, Button, TextField } from "@mui/material";
import { EditButtonIcon, CheckIcon } from "~components/Icons";
import type { Button as IButton } from "~api/stream/interfaces";
import { authFetch } from "~helpers/authFetch";
import { useStreamContext } from "~contexts/Stream";
import { useSocketContext } from "~contexts/Socket";
import { getStyles } from "./styles";

interface Props {
  index: number;
  button: IButton;
  buttonActive: number;
  setButtonActive: (value: number) => void;
  buttons: IButton[];
  setButtons: (value: IButton[]) => void;
}

const CategoryButton = ({
  index,
  button,
  buttons,
  setButtons,
  buttonActive,
  setButtonActive,
}: Props) => {
  const theme = useTheme();
  const { styles } = getStyles(theme);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [isEditing, setEditing] = useState<boolean>(false);
  const [draftLabel, setDraftLabel] = useState<string>(button.label || "");
  const initialLabelRef = useRef<string>((button.label || "").trim());
  const isActive = buttonActive === index;
  const { currentStream } = useStreamContext();
  const { updateStreamSocket } = useSocketContext();

  const onChangeButtonLabel = (text: string) => {
    setDraftLabel(text);
  };

  const saveCategoryLabel = async () => {
    if (!currentStream?.id || !button?.id_button) return;
    const normalizedLabel = draftLabel.trim();
    const currentLabel = initialLabelRef.current;
    if (!normalizedLabel || normalizedLabel === currentLabel) return;

    try {
      await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/stream/${currentStream.id}/category/${button.id_button}`,
        method: "PATCH",
        data: { label: normalizedLabel },
      });
      setButtons(
        buttons.map((btn) =>
          btn.id_button === button.id_button ? { ...btn, label: normalizedLabel } : btn
        )
      );
      setDraftLabel(normalizedLabel);
      updateStreamSocket({ categoriesUpdated: true });
    } catch (error) {
      console.error("Error updating category label:", error);
    }
  };

  const onCloseEditInput = async () => {
    if (isActive) {
      await saveCategoryLabel();
      setEditing(false);
    }
  };

  const onStartEditing = () => {
    initialLabelRef.current = (button.label || "").trim();
    setDraftLabel(button.label || "");
    setEditing(true);
  };

  useEffect(() => {
    if (!isActive) setEditing(false);
  }, [isActive]);

  useEffect(() => {
    setDraftLabel(button.label || "");
  }, [button.label]);

  useEffect(() => {
    if (isActive) {
      buttonRef.current?.scrollIntoView({
        behavior: "smooth",
        inline: isEditing ? "end" : "nearest",
        block: "nearest",
      });
    }
  }, [isActive, isEditing]);

  useEffect(() => {
    const onKeyPressEnter = (e: KeyboardEvent) => {
      if (e.key === "Enter" && isEditing && isActive) {
        onCloseEditInput();
      }
    };

    document.addEventListener("keyup", onKeyPressEnter);

    return () => document.removeEventListener("keyup", onKeyPressEnter);
  }, [isActive, isEditing]);

  return (
    <Button
      tabIndex={-1}
      disableRipple
      ref={buttonRef}
      disableFocusRipple
      variant="categoryButton"
      onClick={() => setButtonActive(index)}
      className={`${isActive ? "isActive" : ""} ${isEditing && isActive ? "isEditing" : ""}`}
      endIcon={
        isActive && !isEditing ? (
          <EditButtonIcon onClick={onStartEditing} />
        ) : (
          <CheckIcon onClick={onCloseEditInput} />
        )
      }
    >
      {isEditing && isActive ? "" : button.label}

      {isEditing && isActive && (
        <TextField
          autoFocus
          variant="standard"
          value={draftLabel}
          onChange={(e) => onChangeButtonLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onCloseEditInput();
            }
          }}
          sx={styles.textField}
        />
      )}
    </Button>
  );
};

export default CategoryButton;
